import { useOrders } from "@/hooks/useOrders";
import { Link, Navigate, useParams, useSearchParams } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import type { Account, Backtest, BacktestAccount, OrderStats } from "@/api/types";
import { useApi } from "../context/ApiContext";
import { Card, CardHead, ExchangeMark, Pill, Tag } from "../components/ui/kit";
import { OrderLedger } from "../components/OrderLedger";
import { EquityCurve } from "../components/charts/ResultsCharts";
import { LiveScriptPanel } from "../components/live/LiveScriptPanel";
import { LiveTradesChart } from "../components/live/LiveTradesChart";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { Dialog } from "../components/ui/Dialog";
import { CandlestickChart, Code2, SlidersHorizontal } from "lucide-react";
import { BacktestAccountModal, type AccountLinkPayload } from "../components/BacktestAccountModal";
import type { EquityPoint } from "@/lib/backtest-stats";
import { realizedPnlSeries } from "@/lib/order-trades";
import { accountKindLabel, providerLabel } from "@/lib/account";
import { defaultStartDate, engineType, isLabInstance, marketLabel } from "@/lib/live";
import { toDateTime } from "@/lib/backtest-run";


/** Orders of the strategy loaded for its activity list and P&L curve. */
const ORDER_HISTORY = 1000;

const money = new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 });
const signed = (n: number) => `${n >= 0 ? "+" : "−"}$${money.format(Math.abs(n))}`;
const tone = (n: number) => (n > 0 ? "var(--success)" : n < 0 ? "var(--danger)" : "var(--text-primary)");

function runningFor(since?: string | null): string {
  if (!since) return "";
  const ms = Date.now() - new Date(since.replace(" ", "T") + (/[zZ]|[+-]\d\d:?\d\d$/.test(since) ? "" : "Z")).getTime();
  if (!Number.isFinite(ms) || ms < 0) return "";
  const days = Math.floor(ms / 86_400_000);
  if (days >= 1) return `${days}d`;
  const hours = Math.floor(ms / 3_600_000);
  return hours >= 1 ? `${hours}h` : `${Math.max(1, Math.floor(ms / 60_000))}m`;
}

export default function LiveDetail() {
  const { id } = useParams();
  const [params, setParams] = useSearchParams();
  const api = useApi(["accounts", "backtests"]);
  /** The script and the exchange settings open over the page: the editor would otherwise catch the page's scrolling. */
  const [scriptOpen, setScriptOpen] = useState(false);
  const [exchangesOpen, setExchangesOpen] = useState(false);
  const [chartOpen, setChartOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmStop, setConfirmStop] = useState(false);
  const [accountModal, setAccountModal] = useState<"add" | "edit" | null>(null);
  const [editingAccount, setEditingAccount] = useState<BacktestAccount | null>(null);
  const [removeAccountId, setRemoveAccountId] = useState<string | null>(null);
  const [stats, setStats] = useState<OrderStats | null>(null);
  const [statsLoaded, setStatsLoaded] = useState(false);
  /** The strategy's own exchange orders, newest first: the activity list and the P&L curve. */
  const backtest: Backtest | undefined = api.backtests.find((b: Backtest) => b.id === id);

  const linked: BacktestAccount[] = useMemo(() => backtest?.accounts || [], [backtest?.accounts]);
  // On several exchanges, ?account= picks whose orders and results show (the Live card's own).
  const wanted = params.get("account");
  const accountId = (linked.find((a) => a.account_id === wanted) || linked[0])?.account_id || "";
  const account: Account | undefined = api.accounts.find((a: Account) => a.id === accountId);
  const connectedIds = useMemo(() => new Set(linked.map((a) => a.account_id)), [linked]);

  const orders = useOrders({ account_id: accountId, backtest_id: id, limit: ORDER_HISTORY }, api.ready && !!accountId && !!id).rows;

  // Real results come from the orders this strategy placed on the exchange.
  useEffect(() => {
    if (!id || !accountId) return;
    let cancelled = false;
    setStatsLoaded(false);
    api
      .commands.getOrderStats({ account_id: accountId, backtest_id: id })
      .then((s: unknown) => !cancelled && setStats((s as OrderStats) || null))
      .catch(() => !cancelled && setStats(null))
      .finally(() => !cancelled && setStatsLoaded(true));
    return () => {
      cancelled = true;
    };
  }, [id, accountId, api.ready, api.commands, orders]);

  // Realized P&L over time, from the trades this strategy closed on the exchange.
  const pnlCurve: EquityPoint[] = useMemo(
    () => realizedPnlSeries(orders).map((p, i) => ({ date: new Date(p.t).toISOString(), t: p.t, pnl: p.pnl, isTrade: i > 0 })),
    [orders]
  );

  if (!api.ready) return <Card className="py-8 text-center text-sm text-[var(--text-muted)]">Loading…</Card>;
  if (!id || id === "spot" || id === "futures" || id === "new") return <Navigate to="/" replace />;
  if (!backtest) return <Card className="py-8 text-center text-sm text-[var(--text-muted)]">Live strategy not found.</Card>;
  if (isLabInstance(backtest)) return <Navigate to={`/lab/backtest/${id}`} replace />;
  const bt = backtest;

  async function setEnabled(next: boolean) {
    setBusy(true);
    setError(null);
    try {
      await api.commands.updateBacktest(next
          ? { id: bt.id, start_date: toDateTime(bt.start_date) || defaultStartDate(bt), end_date: null, enabled: true }
          : { id: bt.id, enabled: false }
      );
      setConfirmStop(false);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Couldn't update the strategy");
    } finally {
      setBusy(false);
    }
  }

  async function saveAccountLink(payload: AccountLinkPayload) {
    const body = {
      backtest_id: bt.id,
      account_id: payload.accountId,
      bypass: payload.bypass,
      reduce_only: payload.reduceOnly,
      crypto_limit: payload.cryptoLimit,
      fiat_limit: payload.fiatLimit,
      slippage: payload.slippage,
    };
    await (accountModal === "edit" ? api.commands.updateBacktestAccount : api.commands.addBacktestAccount)(body);
  }

  async function handleRemoveAccount() {
    if (!removeAccountId) return;
    setBusy(true);
    setError(null);
    try {
      await api.commands.removeBacktestAccount({ backtest_id: bt.id, account_id: removeAccountId });
      setRemoveAccountId(null);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Couldn't remove the exchange");
    } finally {
      setBusy(false);
    }
  }

  // Created but never started (it has placed no order): the Start button asks for attention.
  const neverStarted = !bt.enabled && Boolean(accountId) && statsLoaded && (stats?.health?.counts?.total ?? 0) === 0;
  const running = bt.enabled && bt.status === "running";
  const realPnl = stats ? Number(stats.trading?.realized_pnl) : NaN;
  const closedTrades = stats?.trading?.trades ?? 0;
  // The curve runs to now while the strategy trades, else to its last closed trade.
  const pnlEnd = bt.enabled ? new Date().toISOString() : null;
  const statusPillEl = !bt.enabled ? (
    <Pill tone="mute">Stopped</Pill>
  ) : bt.status === "error" ? (
    <Pill tone="bad">Error</Pill>
  ) : running ? (
    <Pill tone="ok" dot={false}>{`Running${runningFor(bt.start_date) ? ` · ${runningFor(bt.start_date)}` : ""}`}</Pill>
  ) : (
    <Pill tone="info">Starting</Pill>
  );

  return (
    <div className="flex min-w-0 flex-col gap-5">
      {/* Header: name on the left, its results in the middle, Start / Stop on the right. */}
      <div className="grid items-center gap-3.5 lg:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)]">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2.5">
            {running && (
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[var(--success)] opacity-60" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-[var(--success)]" />
              </span>
            )}
            {statusPillEl}
            {account && <Tag>{`${providerLabel(account.provider)} ${accountKindLabel(account)}`}</Tag>}
            <Tag>{marketLabel(bt)}</Tag>
          </div>
          <h1 className="m-0 mt-2 truncate text-[22px] font-semibold tracking-[-0.01em] text-[var(--text-primary)]">{bt.name}</h1>
          <p className="m-0 mt-1 text-[13px] text-[var(--text-muted)]">
            {linked.length > 1 ? (
              <span role="group" aria-label="Exchange account" className="inline-flex flex-wrap gap-1 align-middle">
                {linked.map((row) => (
                  <button
                    key={row.account_id}
                    type="button"
                    aria-pressed={row.account_id === accountId}
                    onClick={() => setParams({ account: row.account_id }, { replace: true })}
                    className={
                      row.account_id === accountId
                        ? "rounded-full border border-[var(--border)] bg-[var(--bg-card-elevated)] px-2.5 py-[2px] text-xs text-[var(--text-primary)]"
                        : "rounded-full border border-[var(--border)] px-2.5 py-[2px] text-xs text-[var(--text-muted)] hover:text-[var(--text-primary)]"
                    }
                  >
                    {api.accounts.find((a: Account) => a.id === row.account_id)?.name || "Account"}
                  </button>
                ))}
              </span>
            ) : account ? (
              <Link to={`/integrations/marketplaces/${account.id}`} className="text-inherit no-underline hover:underline">
                {account.name}
              </Link>
            ) : (
              "No exchange linked"
            )}
            {bt.metadata?.ui?.source_id && (
              <>
                {" · "}
                <Link to={`/lab/backtest/${bt.metadata.ui.source_id}`} className="text-inherit no-underline hover:underline">
                  Open its backtest
                </Link>
              </>
            )}
          </p>
        </div>
        <div
          className="flex items-center gap-5 rounded-card border border-[var(--border)] bg-[var(--bg-card)] px-5 py-2.5 lg:justify-self-center"
          title="From orders filled on the exchange, without fees and funding"
        >
          <div className="text-center">
            <div className="text-[11px] uppercase tracking-[0.06em] text-[var(--text-muted)]">Real P&amp;L</div>
            <div className="num text-[20px] font-semibold" style={{ color: Number.isFinite(realPnl) ? tone(realPnl) : "var(--text-muted)" }}>
              {Number.isFinite(realPnl) ? signed(realPnl) : "—"}
            </div>
          </div>
          <span aria-hidden className="h-9 w-px bg-[var(--border)]" />
          <div className="text-center">
            <div className="text-[11px] uppercase tracking-[0.06em] text-[var(--text-muted)]">Win rate</div>
            <div className="num text-[20px] font-semibold text-[var(--text-primary)]">
              {closedTrades ? `${(Number(stats?.trading?.win_rate || 0) * 100).toFixed(0)}%` : "—"}
              <span className="ml-1.5 text-xs font-normal text-[var(--text-muted)]">
                {closedTrades ? `of ${closedTrades}` : "no trades"}
              </span>
            </div>
          </div>
        </div>
        <div className="flex flex-col items-end gap-2 lg:justify-self-end">
          <div className="flex flex-wrap gap-2">
            {bt.enabled ? (
              <button type="button" className="btn btn-danger-outline" disabled={busy} onClick={() => setConfirmStop(true)}>
                Stop
              </button>
            ) : (
              <button
                type="button"
                className={`btn btn-primary ${neverStarted ? "btn-attention" : ""}`}
                disabled={busy || !accountId}
                onClick={() => void setEnabled(true)}
              >
                Start
              </button>
            )}
          </div>
          {neverStarted && (
            <span className="text-xs text-[var(--text-muted)]">Not trading yet. Press Start to begin.</span>
          )}
          {confirmStop && (
            <div className="flex flex-wrap items-center justify-end gap-2 rounded-[10px] border border-[var(--border)] bg-[var(--bg-card)] px-3 py-2 text-xs text-[var(--text-secondary)]">
              Stop the strategy? It won't place new orders.
              <button type="button" className="btn btn-danger-outline btn-sm" disabled={busy} onClick={() => void setEnabled(false)}>
                Stop
              </button>
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => setConfirmStop(false)}>
                Cancel
              </button>
            </div>
          )}
        </div>
      </div>

      {error && <div className="rounded-[10px] bg-[var(--danger-bg)] px-3 py-2 text-[13px] text-[var(--danger)]">{error}</div>}
      {bt.status === "error" && bt.status_msg && (
        <div className="rounded-[12px] border border-[color-mix(in_srgb,var(--danger)_40%,var(--border))] bg-[color-mix(in_srgb,var(--danger)_10%,transparent)] px-3.5 py-3 text-[13px] text-[var(--text-secondary)]">
          <b className="text-[var(--danger)]">The strategy stopped with an error.</b> {bt.status_msg}
        </div>
      )}
      {/* The P&L curve and the position it holds now, side by side on wide screens. */}
      <div className={`grid gap-4 ${accountId ? "lg:grid-cols-2" : ""}`}>
        <Card>
          <CardHead title="Real P&L">
            <span className="text-xs text-[var(--text-muted)]">Realized on the exchange, without fees and funding</span>
          </CardHead>
          {pnlCurve.length > 1 ? (
            <div className="px-3 pb-3">
              <EquityCurve points={pnlCurve} endDate={pnlEnd} className="h-[260px] w-full" />
              {orders.length >= ORDER_HISTORY && (
                <div className="px-1 pt-1 text-[11px] text-[var(--text-muted)]">From the latest {ORDER_HISTORY.toLocaleString()} orders.</div>
              )}
            </div>
          ) : (
            <div className="px-4 py-12 text-center text-[13px] text-[var(--text-muted)]">
              The curve appears when the first trade closes on the exchange.
            </div>
          )}
        </Card>
        {accountId && (
          <LiveTradesChart backtest={bt} accountId={accountId} chartOpen={chartOpen} onCloseChart={() => setChartOpen(false)} />
        )}
      </div>

      <Card>
        <div className="flex flex-wrap items-center gap-2 border-b border-[var(--border)] px-4 py-2.5">
          <b className="text-[13px] font-semibold text-[var(--text-primary)]">Exchange orders</b>
          <div className="ml-auto flex flex-wrap gap-1">
            {accountId && (
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => setChartOpen(true)}>
                <CandlestickChart size={14} />
                Show chart
              </button>
            )}
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => setExchangesOpen(true)}>
              <SlidersHorizontal size={14} />
              Exchange settings
            </button>
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => setScriptOpen(true)}>
              <Code2 size={14} />
              View script
            </button>
          </div>
        </div>

        {accountId ? (
          <div className="p-3">
            <OrderLedger accountId={accountId} fixedBacktestId={bt.id} backtests={api.backtests} compact title="" />
          </div>
        ) : (
          <div className="px-4 py-10 text-center text-[13px] text-[var(--text-muted)]">Link an exchange in Exchange settings to place orders.</div>
        )}
      </Card>

      {/* Before the account modal and the confirm dialog, so those open on top of it. */}
      {exchangesOpen && (
        <Dialog title="Exchange settings" className="max-w-[640px]" onClose={() => setExchangesOpen(false)}>
          <div className="flex flex-col gap-4">
            <div>
              <div className="mb-2 flex items-center justify-between gap-2">
                <b className="text-[13px] text-[var(--text-primary)]">Linked exchanges</b>
                <button
                  type="button"
                  className="btn btn-soft btn-sm"
                  onClick={() => {
                    setEditingAccount(null);
                    setAccountModal("add");
                  }}
                >
                  + Add exchange
                </button>
              </div>
              {linked.length === 0 ? (
                <div className="text-[13px] text-[var(--text-muted)]">No exchange linked, so no orders are placed.</div>
              ) : (
                <div className="flex flex-col rounded-[12px] border border-[var(--border)]">
                  {linked.map((row) => {
                    const acc = api.accounts.find((a: Account) => a.id === row.account_id);
                    const size = row.bypass
                      ? "Script's order size"
                      : [row.fiat_limit && `money ≤ ${row.fiat_limit}`, row.crypto_limit && `coins ≤ ${row.crypto_limit}`].filter(Boolean).join(", ") || "Capped";
                    return (
                      <div key={row.account_id} className="flex flex-wrap items-center gap-3 border-t border-[var(--divider)] px-3.5 py-3 first:border-t-0">
                        {acc && <ExchangeMark provider={acc.provider} />}
                        <div className="min-w-0 flex-1">
                          <b className="block text-[13px] font-semibold text-[var(--text-primary)]">{acc?.name || row.account_id}</b>
                          <span className="block text-xs text-[var(--text-muted)]">
                            {[size, row.reduce_only ? "reduce-only exits" : null, row.slippage ? `limit ±${row.slippage}%` : "market orders"]
                              .filter(Boolean)
                              .join(" · ")}
                          </span>
                        </div>
                        <button
                          type="button"
                          className="btn btn-soft btn-sm"
                          onClick={() => {
                            setEditingAccount(row);
                            setAccountModal("edit");
                          }}
                        >
                          Edit
                        </button>
                        <button type="button" className="btn btn-ghost btn-sm" onClick={() => setRemoveAccountId(row.account_id)}>
                          Remove
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="border-t border-[var(--divider)] pt-4 text-[13px] text-[var(--text-muted)]">
              Started from <span className="num text-[var(--text-secondary)]">{toDateTime(bt.start_date)}</span> UTC
            </div>
          </div>
        </Dialog>
      )}

      <BacktestAccountModal
        open={accountModal != null}
        mode={accountModal === "edit" ? "edit" : "add"}
        accounts={api.accounts || []}
        connectedAccountIds={connectedIds}
        preferFutures={engineType(bt) === "futures"}
        editingAccount={editingAccount}
        onClose={() => {
          setAccountModal(null);
          setEditingAccount(null);
        }}
        onSave={saveAccountLink}
      />

      {scriptOpen && (
        <Dialog title="Script" className="max-w-[1000px]" onClose={() => setScriptOpen(false)}>
          <LiveScriptPanel backtest={bt} editorHeight="min(62vh, 640px)" />
        </Dialog>
      )}
      <ConfirmDialog
        open={Boolean(removeAccountId)}
        title="Remove this exchange?"
        description="The strategy stops placing orders there until you add it again."
        confirmLabel="Remove"
        loading={busy}
        onCancel={() => setRemoveAccountId(null)}
        onConfirm={() => void handleRemoveAccount()}
      />

    </div>
  );
}
